// Excel slips: one sheet per satsang ghar, and one sheet per SK/SR/पाठी (visiting special SKs excluded).
import * as XLSX from "xlsx-js-style";
import {
  GHARS,
  PREACHER_BY_ID,
  PATHI_BY_ID,
  WEEKDAYS_HI,
  MONTHS_HI,
  AREA_INFO,
  GHAR_TYPE_HI,
  cellTime,
  withHonorific,
  noteLines,
} from "../data/rssbData";

const FONT = "Nirmala UI";
const thin = { style: "thin", color: { rgb: "000000" } };
const BORDER = { top: thin, bottom: thin, left: thin, right: thin };
const st = (sz, bold, h = "center", border = true) => ({
  font: { name: FONT, sz, bold },
  alignment: { horizontal: h, vertical: "center", wrapText: true },
  ...(border ? { border: BORDER } : {}),
});
const fmtDate = (iso) => iso.split("-").reverse().join("/");
const DASH = "----------";

const gharLabel = (g) => `${g.hi}–${GHAR_TYPE_HI[g.type]}`;
const norm = (s) => (s || "").replace(/[\sािीुूंँ़–-]/g, "").replace(/झ/g, "ज");
const address = (place) => {
  const n = norm(place);
  const g = n && (GHARS.find((x) => norm(x.hi) === n) || GHARS.find((x) => norm(x.hi).startsWith(n) || n.startsWith(norm(x.hi))));
  return g ? gharLabel(g) : place || "";
};
const timeOf = (cell, d) => cellTime(cell, d.iso);

// Person info for the SK/SR or पाठी side of a cell.
function personOf(cell, kind) {
  if (kind === "sk") {
    if (cell.type === "SPECIAL") {
      const s = cell.special || {};
      return { key: null, name: `${s.name || ""}${s.fromCity ? `–${s.fromCity}` : ""}`, addr: s.fromCity || "", mobile: s.mobile || "" };
    }
    const p = PREACHER_BY_ID[cell.preacherId];
    if (p) return { key: p.id, p, name: `${withHonorific(p)}–${p.placeHi}`, addr: address(cell.preacherCity || p.placeHi), mobile: cell.preacherMobile || p.mobile || "" };
    if (cell.customPreacher) return { key: `c:${cell.customPreacher}`, name: cell.customPreacher, addr: address(cell.preacherCity), mobile: cell.preacherMobile || "" };
    return null;
  }
  const p = PATHI_BY_ID[cell.pathiId];
  if (p) return { key: p.id, p, name: `${withHonorific(p)}–${p.placeHi}`, addr: address(cell.pathiCity || p.placeHi), mobile: cell.pathiMobile || p.mobile || "" };
  if (cell.customPathi) return { key: `c:${cell.customPathi}`, name: cell.customPathi, addr: address(cell.pathiCity), mobile: cell.pathiMobile || "" };
  return null;
}

// Build one slip sheet: 2 title rows, header, body rows (already arrays), note + signature.
function slipSheet(title2, header, body, widths, rowMerges = []) {
  const n = header.length;
  const aoa = [[`राधा–स्वामी सत्संग, ब्यास ${AREA_INFO.areaHi} एरिया`], [title2], header, ...body.map((r) => r.cells)];
  const merges = [{ s: { r: 0, c: 0 }, e: { r: 0, c: n - 1 } }, { s: { r: 1, c: 0 }, e: { r: 1, c: n - 1 } }, ...rowMerges];
  const styles = {};
  styles["0:0"] = st(14, true);
  styles["1:0"] = st(13, true);
  header.forEach((_, c) => (styles[`2:${c}`] = st(11, true)));
  body.forEach((r, i) => r.cells.forEach((_, c) => (styles[`${i + 3}:${c}`] = st(10, false, r.left?.includes(c) ? "left" : "center"))));

  let r = aoa.length + 1;
  noteLines(MONTH_REF.m).forEach((line, i) => {
    aoa[r] = [`${i === 0 ? "नोट: " : ""}${i + 1}. ${line}`];
    merges.push({ s: { r, c: 0 }, e: { r, c: n - 1 } });
    styles[`${r}:0`] = st(10, true, "left", false);
    r++;
  });
  r++;
  [AREA_INFO.secretaryNameHi && `(${AREA_INFO.secretaryNameHi})`, AREA_INFO.secretaryDesignationHi, AREA_INFO.phones].filter(Boolean).forEach((line) => {
    aoa[r] = [];
    aoa[r][Math.max(0, n - 3)] = line;
    merges.push({ s: { r, c: Math.max(0, n - 3) }, e: { r, c: n - 1 } });
    styles[`${r}:${Math.max(0, n - 3)}`] = st(11, true, "center", false);
    r++;
  });

  const ws = XLSX.utils.aoa_to_sheet(aoa.map((row) => row || []));
  Object.entries(styles).forEach(([k, s]) => {
    const [rr, cc] = k.split(":").map(Number);
    const ref = XLSX.utils.encode_cell({ r: rr, c: cc });
    if (!ws[ref]) ws[ref] = { t: "s", v: "" };
    ws[ref].s = s;
  });
  ws["!merges"] = merges;
  ws["!cols"] = widths.map((wch) => ({ wch }));
  ws["!rows"] = [{ hpt: 24 }, { hpt: 22 }, { hpt: 34 }, ...body.map(() => ({ hpt: 32 }))];
  ws["!pageSetup"] = { orientation: "portrait", fitToWidth: 1 };
  return ws;
}

const MONTH_REF = { m: 0 };
const sheetName = (name, used) => {
  let base = name.replace(/[[\]:*?/\\]/g, "").slice(0, 28) || "Sheet";
  let s = base;
  for (let i = 2; used.has(s); i++) s = `${base.slice(0, 26)}-${i}`;
  used.add(s);
  return s;
};

export function exportGharSlips(variant, dates, year, month) {
  MONTH_REF.m = month;
  const wb = XLSX.utils.book_new();
  const used = new Set();
  GHARS.forEach((g) => {
    const body = [];
    const merges = [];
    dates.forEach((d) => {
      const cell = variant.cells[g.id]?.[d.iso];
      if (!cell) return;
      const r = body.length + 3;
      const isCd = cell.type === "CD" || cell.type === "LCD";
      const sk = isCd ? { name: cell.type === "CD" ? "सी डी सत्संग" : "सवाल–जवाब सत्संग", addr: "--", mobile: "--" } : personOf(cell, "sk") || {};
      const pa = isCd ? { name: DASH, addr: DASH, mobile: DASH } : personOf(cell, "pathi") || {};
      body.push({ cells: [fmtDate(d.iso), WEEKDAYS_HI[d.dow], timeOf(cell, d), "स.क.", sk.name || "", sk.addr || "", sk.mobile || ""], left: [4, 5] });
      body.push({ cells: ["", "", "", "पाठी", pa.name || "", pa.addr || "", pa.mobile || ""], left: [4, 5] });
      [0, 1, 2].forEach((c) => merges.push({ s: { r, c }, e: { r: r + 1, c } }));
    });
    const ws = slipSheet(
      `सत्संग प्रोग्राम– ${gharLabel(g)}, माह ${MONTHS_HI[month]}–${year}`,
      ["तारीख", "वार", "सत्संग समयः–", "स.क./पाठी", "सत्संग कर्ता/पाठी का नाम", "पता", "संपर्क नंबर"],
      body,
      [12, 9, 11, 8, 30, 18, 14],
      merges
    );
    XLSX.utils.book_append_sheet(wb, ws, sheetName(g.hi, used));
  });
  XLSX.writeFile(wb, `RSSB_Satsang_Ghar_Slips_${MONTHS_HI[month]}_${year}.xlsx`);
}

export function exportPersonSlips(variant, dates, year, month) {
  MONTH_REF.m = month;
  const people = new Map(); // key -> {role, name, rows}
  GHARS.forEach((g) =>
    dates.forEach((d) => {
      const cell = variant.cells[g.id]?.[d.iso];
      if (!cell || cell.type === "CD" || cell.type === "LCD") return;
      [["sk", cell.type === "SPECIAL" ? null : "स.क."], ["pathi", "पाठी"]].forEach(([kind, role]) => {
        if (!role) return;
        const info = personOf(cell, kind);
        if (!info?.key) return;
        if (!people.has(info.key)) people.set(info.key, { role, name: info.name, rows: [] });
        people.get(info.key).rows.push({ d, cell, g });
      });
    })
  );
  const ordered = [...people.values()].sort((a, b) => (a.role === b.role ? 0 : a.role === "स.क." ? -1 : 1));
  const wb = XLSX.utils.book_new();
  const used = new Set();
  ordered.forEach((pp) => {
    pp.rows.sort((a, b) => a.d.iso.localeCompare(b.d.iso));
    const roleWord = pp.role === "पाठी" ? "पाठी" : "सत्संग कर्ता";
    const body = pp.rows.map(({ d, cell, g }, i) => ({
      cells: [i === 0 ? pp.name : "", fmtDate(d.iso), WEEKDAYS_HI[d.dow], timeOf(cell, d), gharLabel(g), g.secretaryHi || "", g.secretaryMobile || ""],
      left: [0, 4, 5],
    }));
    const merges = body.length > 1 ? [{ s: { r: 3, c: 0 }, e: { r: 2 + body.length, c: 0 } }] : [];
    const ws = slipSheet(
      `सत्संग प्रोग्राम– ${roleWord} ${pp.name}, माह ${MONTHS_HI[month]}–${year}`,
      [roleWord, "तारीख", "वार", "सत्संग समयः–", "सत्संग जगह का नाम", "सेक्रेटरी का नाम", "संपर्क नंबर"],
      body,
      [24, 12, 9, 11, 18, 18, 14],
      merges
    );
    XLSX.utils.book_append_sheet(wb, ws, sheetName(pp.name.split("–")[0].replace(/^(श्रीमती|श्री|कुमारी)\s+/, ""), used));
  });
  XLSX.writeFile(wb, `RSSB_Sewadar_Slips_${MONTHS_HI[month]}_${year}.xlsx`);
}
